import React from 'react';
import isEmpty from 'lodash/isEmpty';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useIntl } from 'react-intl';
import * as Sentry from '@sentry/browser';
import * as actionCreators from 'actions/conversation';
import { updateSelfEvents } from 'actions/index';
import { userData, events as eventsSelector, eventsLoading } from 'selectors/user';
import { list as tagsList, tagsAlreadyFetched } from 'modules/tags/redux/selectors';
import { postTagRequest, fetchTagsRequest } from 'modules/tags/redux/actionCreators';
import { DASHBOARD_REQUEST_REVIEW_NOTIFICATION } from 'constants/api';
import Api from 'api';
import Main from 'components/templates/Main';
import Success from 'components/shared/Success';
import MessageTab from '../components/MessageTab';
import ConversationTabs from '../components/ConversationTabs';
import ShowMoreButton from '../components/ShowMoreButton';
import TabRightMenu from '../components/TabRightMenu';

const View = () => {
  const intl = useIntl();
  const [isTagOpen, setIsTagOpen] = React.useState(false);
  const [cottagesToAskForReview, setCottagesToAskForReview] = React.useState([]);
  const [askReviewDialogOpen, setAskReviewDialogOpen] = React.useState(false);
  const { conversationId } = useParams();
  const dispatch = useDispatch();
  const currentUser = useSelector(userData);
  const tags = useSelector(tagsList);
  const tagsFetched = useSelector(tagsAlreadyFetched);
  const events = useSelector(eventsSelector);
  const isLoadingEvents = useSelector(eventsLoading);

  const { isFetching, error, success, isPosting, data } = useSelector(
    (state) => state.conversation,
  );

  const canAskForReview = async () => {
    try {
      const results = await Api.fetchConversationCanAskForReview(conversationId);

      setCottagesToAskForReview(results);
    } catch (err) {
      Sentry.captureException(err);
    }
  };

  React.useEffect(() => {
    if (!tagsFetched) {
      dispatch(fetchTagsRequest());
    }

    if (!isFetching) {
      dispatch(actionCreators.fetchConversationRequest({ conversationId }));

      canAskForReview();
    }

    return () => {
      dispatch(actionCreators.clearConversation());
    };
  }, []);

  const checkMessageSeen = () => {
    const msgs = data.Messages;
    const userId = currentUser.id;

    if (msgs.length === 0) {
      return;
    }

    const firstUnread = msgs.find(
      (msg) => msg.user_id !== userId && !msg.seen_by_users.includes(userId),
    );

    if (!firstUnread) {
      return;
    }

    dispatch(
      actionCreators.postMessageSeenRequest({
        conversationId: firstUnread.conversation_id,
        messageId: firstUnread.id,
      }),
    );
  };

  React.useEffect(() => {
    if (data && !isEmpty(data)) {
      checkMessageSeen();
    }
  }, [data]);

  const askForReviewNotificationClick = () => {
    dispatch(
      updateSelfEvents({
        events: [DASHBOARD_REQUEST_REVIEW_NOTIFICATION],
      }),
    );
  };

  const askForReview = async (body) => {
    await Api.postAskForReviewMessage(conversationId, body);

    setCottagesToAskForReview(
      cottagesToAskForReview.filter((c) => c.id !== parseInt(body.cottageId, 10)),
    );

    dispatch(actionCreators.fetchConversationRequest({ conversationId }));
  };

  const changeTags = (_tags, showArchivedMsg) => {
    const newTag = _tags.find((tag) => typeof tag.value === 'string');
    const { id } = data;

    if (newTag) {
      dispatch(postTagRequest({ id, tag: newTag.value, tags: _tags }));
    } else {
      dispatch(actionCreators.postConversationTagsRequest({ id, tags: _tags, showArchivedMsg }));
    }
  };

  const toggleArchived = () => {
    let newTags = data.tags.filter(({ id }) => id !== 3);

    if (newTags.length === data.tags.length) {
      const archived = tags.find(({ id }) => id === 3);
      newTags = newTags.concat([archived]);
    }

    changeTags(
      newTags.map(({ id, tag }) => ({ value: id, label: tag })),
      true,
    );
  };

  const reviewHost = async (_data) => {
    const res = await Api.postHostReview(conversationId, _data);

    dispatch(actionCreators.updateConversationReview(res));
  };

  const shouldOpenAskReviewNotification =
    !isLoadingEvents && !events[DASHBOARD_REQUEST_REVIEW_NOTIFICATION];
  const isLoading = isFetching || isEmpty(data) || isEmpty(currentUser);
  const showLoader = isLoading || !isEmpty(error);
  const loaderProps = { showLoader, isPosting };
  const isHost = currentUser.has_reviewed_cottages;

  let displayName = '';
  let otherUsers = [];

  if (!isLoading) {
    otherUsers = data.Users.filter((user) => user.id !== currentUser.id);
    displayName = otherUsers.map((user) => user.name).join(', ');

    if (displayName === 'Deleted') {
      displayName = intl.formatMessage({ id: 'Deleted', defaultMessage: 'Deleted' });
    }
  }

  return (
    <Main
      title={displayName}
      extra={
        data && data?.filters?.commission ? null : (
          <ShowMoreButton data={data} isFetching={isFetching} currentUser={currentUser} />
        )
      }
    >
      <ConversationTabs
        tab={1}
        rightMenu={
          <TabRightMenu
            {...{
              currentUser,
              otherUsers,
              cottagesToAskForReview,
              isHost,
              shouldOpenAskReviewNotification,
              askForReviewNotificationClick,
              askReviewDialogOpen,
              setAskReviewDialogOpen,
              askForReview,
              tags,
              toggleArchived,
              data,
              isTagOpen,
              setIsTagOpen,
              changeTags,
              reviewHost,
            }}
          />
        }
      />
      <MessageTab
        {...loaderProps}
        {...{
          data,
          isHost,
          conversationId,
          currentUser,
          showNoteMessage: !isHost && data?.filters?.commission === true,
        }}
        deleteMessage={(p) => dispatch(actionCreators.deleteMessageRequest(p))}
      />
      <Success success={success} onClose={() => dispatch(actionCreators.clearConversation())} />
    </Main>
  );
};

export default View;
